<template>
  <span class="feature-tag" :style="{'--bg-color': tagData.backgroundColor, '--font-color': tagData.fontColor}">
    <slot>
      <span class="text--bold d-flex align-center" :class="size === 'sm' ? 'text--x-small' : 'text--small'">
        <template v-if="tagData.icon">
          <app-material-icon :name="tagData.icon" :size="size === 'sm' ? 'xxs' : 'xs'" color="inherit" class="mr-1" />
        </template>
        <span>{{ $t(`common.AppFeatureTag.textKeys.${tagData.textKey}`) }}</span>
      </span>
    </slot>
  </span>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { useTheme } from 'vuetify'

export type FeatureTag = 'beta' | 'new' | 'upcoming' | 'upgrade'
type FeatureTagSize = 'sm' | 'md'

type TagData = {
  backgroundColor: string;
  fontColor: string;
  icon?: string;
  textKey: string;
}

export default defineComponent({
  name: 'AppFeatureTag',
  setup () {
    const { current: theme } = useTheme()

    return {
      betaColor: {
        background: theme.value.colors['lime-lighten-3'],
        font: theme.value.colors['lime-darken-2'],
      },
      newColor: {
        background: theme.value.colors['dark-blue-lighten-4'],
        font: theme.value.colors['dark-blue-darken-2'],
      },
      upcomingColor: {
        background: theme.value.colors['grey-50'],
        font: theme.value.colors['grey-700'],
      },
      upgradeColor: {
        background: theme.value.colors['gold-100'],
        font: theme.value.colors['gold-700'],
      },
    }
  },
  props: {
    size: {
      type: String as PropType<FeatureTagSize>,
      default: 'md',
    },
    tag: {
      type: String as PropType<FeatureTag>,
      required: true,
    },
  },
  computed: {
    tagData (): TagData {
      const data = {
        beta: {
          backgroundColor: this.betaColor.background,
          fontColor: this.betaColor.font,
          textKey: 'beta',
        },
        new: {
          backgroundColor: this.newColor.background,
          fontColor: this.newColor.font,
          textKey: 'new',
        },
        upcoming: {
          backgroundColor: this.upcomingColor.background,
          fontColor: this.upcomingColor.font,
          textKey: 'upcoming',
        },
        upgrade: {
          backgroundColor: this.upgradeColor.background,
          fontColor: this.upgradeColor.font,
          icon: 'crown',
          textKey: 'upgrade',
        },
      }

      return data[this.tag]
    },
  },
})
</script>

<style scoped lang="scss">
.feature-tag {
  background: var(--bg-color);
  color: var(--font-color);
  text-transform: none;
  letter-spacing: normal;

  user-select: none;
  border-radius: 4px;
  padding: 2px 8px;
}
</style>
